import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { Link } from '@inertiajs/react';
import { hexToRgba, isColorDark, resolveLinkProps, buildSrcSet } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';

export default function LandingHeader({ block, brand, navLinks, primaryColor, secondaryColor, enableAnimations = true, forceScrolled }) {
    const hc = block?.config || {};
    const variant = hc.variant || 'transparent';
    const showLogo = hc.show_logo !== false;
    const showNav = hc.show_nav !== false;
    const showPhone = hc.show_phone === true;
    const height = hc.height || 'normal';
    const hasShadow = hc.shadow !== false;
    const hasBlur = hc.blur !== false;
    const headerMode = hc.header_mode || 'overlay';
    const navStyle = hc.nav_style || 'default';
    const mobileStyle = hc.mobile_style || 'drawer-right';
    const logoStyle = hc.logo_style || 'default';
    const scrollBehavior = hc.scroll_behavior || 'default';
    const showProgressBar = hc.show_progress_bar !== false;
    const borderStyle = hc.border_style || 'none';
    const logoSizeRaw = hc.logo_size || 'normal';
    const logoSizePx = typeof logoSizeRaw === 'number' ? logoSizeRaw : (logoSizeRaw === 'small' ? 24 : logoSizeRaw === 'large' ? 64 : 40);
    const logoSizeMobilePx = hc.logo_size_mobile || Math.min(logoSizePx, 36);
    const navSeparator = hc.nav_separator || 'none';
    const announcementText = hc.announcement_text || '';
    const navFontSize = hc.nav_font_size || 'sm';
    const navFontWeight = hc.nav_font_weight || 'medium';
    const navUppercase = !!hc.nav_uppercase;
    const activeHighlight = !!hc.active_highlight;
    const maxWidthClass = hc.max_width === 'narrow' ? 'max-w-5xl' : hc.max_width === 'wide' ? 'max-w-screen-2xl' : hc.max_width === 'full' ? 'max-w-full' : 'max-w-7xl';
    const paddingXClass = hc.padding_x === 'tight' ? 'px-2 sm:px-4' : hc.padding_x === 'wide' ? 'px-6 sm:px-8 lg:px-12' : 'px-4 sm:px-6 lg:px-8';
    const headerRadius = hc.header_radius ?? 0;
    const radiusStyle = headerRadius > 0 ? { borderRadius: headerRadius >= 9999 ? '9999px' : `${headerRadius}px` } : {};
    const radiusWrapClass = headerRadius > 0 ? 'overflow-hidden' : '';
    const hamburgerStyle = hc.hamburger_style || 'cross';
    const navHoverEffect = hc.nav_hover_effect || 'none';
    const logoShrink = !!hc.logo_shrink;
    const navFontFamily = hc.nav_font_family || '';
    const showSearch = !!hc.show_search;
    const announcementTexts = hc.announcement_texts || [];
    const stickyCtaText = hc.sticky_cta_text || '';
    const stickyCtaLink = hc.sticky_cta_link || '';
    const blurOnScroll = !!hc.blur_on_scroll;
    const shrinkScale = hc.shrink_scale || 80;
    const languages = hc.languages || [];
    const ctaPulse = !!hc.cta_pulse;
    const navEntrance = !!hc.nav_entrance;
    const smartCta = hc.smart_cta || null;
    const showDarkToggle = !!hc.show_dark_toggle;
    const liquidFill = !!hc.liquid_fill;
    const gradientBorder = !!hc.gradient_border;
    const noiseGlass = !!hc.noise_glass;
    const hapticFeedback = !!hc.haptic_feedback;
    const socialProofText = hc.social_proof_text || '';
    const utmCtaMap = hc.utm_cta_map || null;
    const returningVisitorCta = hc.returning_visitor_cta || '';

    // Dynamic buttons — backward compat with old cta_text/cta_link
    const buttons = hc.buttons && hc.buttons.length > 0
        ? hc.buttons
        : hc.cta_text ? [{ text: hc.cta_text, link: hc.cta_link || '#contact', style: 'solid', icon: '' }] : [];
    const announcementLink = hc.announcement_link || '';
    const announcementBg = hc.announcement_bg || primaryColor;
    const announcementDismissible = !!hc.announcement_dismissible;

    // Initial style: how the header looks BEFORE scroll (decoupled from scroll_behavior)
    const initialStyle = hc.initial_style || (variant === 'solid' ? 'solid' : variant === 'colored' ? 'solid' : variant === 'glass' ? 'blur' : 'transparent');
    const startsOpaque = initialStyle === 'solid' || initialStyle === 'blur';

    const [scrolledInternal, setScrolledInternal] = useState(!enableAnimations || startsOpaque);
    const scrolled = forceScrolled !== undefined ? forceScrolled : scrolledInternal;
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [headerVisible, setHeaderVisible] = useState(true);
    const [activeSection, setActiveSection] = useState('');
    const [announcementClosed, setAnnouncementClosed] = useState(false);
    const [scrollY, setScrollY] = useState(0);

    // ESC key to close mobile menu + language dropdown
    useEffect(() => {
        if (!mobileOpen) return;
        const handler = (e) => e.key === 'Escape' && setMobileOpen(false);
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [mobileOpen]);

    // Focus trap for mobile menu
    const mobileMenuRef = useRef(null);
    useEffect(() => {
        if (!mobileOpen || !mobileMenuRef.current) return;
        const triggerEl = document.activeElement;
        const nodes = () => [...mobileMenuRef.current.querySelectorAll(FOCUSABLE)];
        setTimeout(() => nodes()[0]?.focus(), 100);
        const handler = (e) => {
            if (e.key !== 'Tab') return;
            const els = nodes(); if (!els.length) return;
            const first = els[0], last = els[els.length - 1];
            if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
                e.preventDefault(); (e.shiftKey ? last : first).focus();
            }
        };
        document.addEventListener('keydown', handler);
        return () => { document.removeEventListener('keydown', handler); triggerEl?.focus(); };
    }, [mobileOpen]);

    // Body scroll lock when mobile menu is open
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    useEffect(() => {
        if (!enableAnimations && startsOpaque) { setScrolledInternal(true); }
        let prevY = 0, ticking = false;
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                const y = window.scrollY;
                if (!startsOpaque) setScrolledInternal(y > 60);
                setScrollProgress((y / (document.documentElement.scrollHeight - window.innerHeight)) * 100);
                if (scrollBehavior === 'hide-show') setHeaderVisible(y < 100 || y < prevY);
                if (scrollBehavior === 'shrink') setScrollY(y);
                if (activeHighlight && navLinks.length > 0) {
                    let found = '';
                    for (const link of navLinks) {
                        if (link.href) continue;
                        const el = document.getElementById(link.id);
                        if (el) { const rect = el.getBoundingClientRect(); if (rect.top <= 120 && rect.bottom > 120) { found = link.id; break; } }
                    }
                    setActiveSection(found);
                }
                prevY = y;
                ticking = false;
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [variant, scrollBehavior, startsOpaque, activeHighlight, navLinks]);

    const smoothScroll = useCallback((e, id) => { e.preventDefault(); setMobileOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }, []);

    // Memoized styles to avoid recreating objects on every render
    const transitionStyle = React.useMemo(() => ({ transition: 'background-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease' }), []);
    const colorVars = React.useMemo(() => ({
        a08: hexToRgba(primaryColor, 0.08), a12: hexToRgba(primaryColor, 0.12),
        a15: hexToRgba(primaryColor, 0.15), a25: hexToRgba(primaryColor, 0.25),
    }), [primaryColor]);

    // All header CSS (injected once)
    const HeaderCss = () => (
        <style>{`
            @keyframes lmCtaPulse{0%,100%{transform:scale(1);box-shadow:0 0 0 0 rgba(255,255,255,0)}12%{transform:scale(1.04);box-shadow:0 0 12px 4px rgba(255,255,255,0.25)}25%,100%{transform:scale(1);box-shadow:0 0 0 0 rgba(255,255,255,0)}}
            .lm-cta-pulse{animation:lmCtaPulse 5s ease-in-out infinite}
            @keyframes marqueeScroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
            @keyframes marqueeBounce{0%,100%{transform:translateX(0)}50%{transform:translateX(-30%)}}
            @keyframes gradientShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
            ${navEntrance ? `@keyframes lmNavIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:none}}.lm-nav-entrance>*{animation:lmNavIn .4s ease-out both}.lm-nav-entrance>*:nth-child(1){animation-delay:50ms}.lm-nav-entrance>*:nth-child(2){animation-delay:100ms}.lm-nav-entrance>*:nth-child(3){animation-delay:150ms}.lm-nav-entrance>*:nth-child(4){animation-delay:200ms}.lm-nav-entrance>*:nth-child(5){animation-delay:250ms}.lm-nav-entrance>*:nth-child(6){animation-delay:300ms}.lm-nav-entrance>*:nth-child(7){animation-delay:350ms}.lm-nav-entrance>*:nth-child(8){animation-delay:400ms}` : ''}
            .lm-liquid-fill{position:relative;overflow:hidden;z-index:0}.lm-liquid-fill::before{content:'';position:absolute;bottom:0;left:0;right:0;height:0;background:currentColor;opacity:0.15;transition:height .35s cubic-bezier(.22,1,.36,1);z-index:-1;border-radius:inherit}.lm-liquid-fill:hover::before{height:100%}
            @keyframes lmGradBorder{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
            .lm-grad-border{height:2px;background:linear-gradient(90deg,${primaryColor},${secondaryColor || primaryColor},${primaryColor});background-size:200% 200%;animation:lmGradBorder 4s ease infinite}
            .lm-noise-glass::after{content:'';position:absolute;inset:0;opacity:0.03;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");pointer-events:none;border-radius:inherit}
            @media(prefers-reduced-motion:reduce){.lm-cta-pulse,.lm-nav-entrance>*,.lm-grad-border{animation:none!important}.lm-liquid-fill::before{transition:none!important}}
            .lm-topbar-scroll:hover{animation-play-state:paused}
            .lm-logo-img{height:${logoSizeMobilePx}px;width:auto;object-fit:contain;transition:all 0.3s}
            @media(min-width:640px){.lm-logo-img{height:${logoSizePx}px}}
        `}</style>
    );


    const hClass = height === 'compact' ? 'h-12' : height === 'large' ? 'h-20' : 'h-16';
    const isColored = variant === 'colored';
    const initialBg = hc.initial_bg || (isColored ? primaryColor : null);
    const bgScrolled = isColored ? primaryColor : (hc.scrolled_bg || hc.bg_color || 'rgba(255,255,255,0.95)');
    // Determine if the current background is "dark" to set text color
    const currentBg = scrolled ? bgScrolled : (initialBg || null);
    const scrolledIsDark = hc.scrolled_text_color ? isColorDark(hc.scrolled_text_color) === false : isColorDark(bgScrolled);
    const initialIsDark = hc.text_color ? isColorDark(hc.text_color) === false : (initialBg ? isColorDark(initialBg) : true);
    const positionClass = headerMode === 'push' ? 'sticky' : 'fixed';

    // Background for the current state — used by variants that transition
    const initialOpacity = (hc.initial_bg_opacity ?? 15) / 100;
    const resolvedInitialBg = initialBg
        ? (initialOpacity < 1 ? hexToRgba(initialBg, initialOpacity) : initialBg)
        : (initialOpacity > 0 ? `rgba(0,0,0,${initialOpacity})` : undefined);
    const currentBgStyle = scrolled
        ? bgScrolled
        : initialStyle === 'solid' ? (initialBg || bgScrolled) : initialStyle === 'blur' ? (resolvedInitialBg || 'rgba(255,255,255,0.1)') : resolvedInitialBg;
    const currentBlur = initialStyle === 'blur' || (scrolled && hasBlur);

    const PhoneLink = ({cls}) => {
        if (!showPhone || !brand.contact_phone) return null;
        return (
            <a href={`tel:${brand.contact_phone}`} className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${cls}`}>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                {brand.contact_phone}
            </a>
        );
    };

    // ── Shared Sub-components ──────────────────────────────

    const logoTextColor = hc.logo_text_color || null;

    const LogoBlock = ({dark, className = ''}) => {
        if (!showLogo) return null;
        const hasShape = logoStyle === 'circle' || logoStyle === 'square';
        const isTextOnly = logoStyle === 'text-only';
        const shrunk = logoShrink && scrolled;
        const currentPx = shrunk ? Math.max(Math.round(logoSizePx * 0.6), 20) : logoSizePx;
        const currentMobilePx = shrunk ? Math.max(Math.round(logoSizeMobilePx * 0.6), 16) : logoSizeMobilePx;
        const shapePx = currentPx + 8;
        const shrinkStyle = shrunk ? {height: `${currentPx}px`} : {};
        const imgClassNormal = `lm-logo-img w-auto object-contain transition-all duration-300`;
        const imgClassShape = `w-full h-full ${logoStyle === 'circle' ? 'rounded-full' : 'rounded-lg'} object-cover`;
        const shapeStyle = hasShape ? {width: `${shapePx}px`, height: `${shapePx}px`, transition: 'all 0.3s'} : {};
        const wrapperClass = logoStyle === 'circle'
            ? `rounded-full overflow-hidden border-2 transition-colors ${dark ? 'border-white/25 bg-white/10' : 'border-gray-200 bg-gray-50'}`
            : logoStyle === 'square'
            ? `rounded-xl overflow-hidden border-2 transition-colors ${dark ? 'border-white/25 bg-white/10' : 'border-gray-200 bg-gray-50'}`
            : '';
        const nameColor = logoTextColor || (dark ? '#ffffff' : primaryColor);
        if (isTextOnly) {
            return (
                <a href="/" className={`flex items-center hover:opacity-80 transition-opacity cursor-pointer min-w-0 no-underline ${className}`}>
                    <span className="text-xl font-bold transition-colors duration-300 truncate" style={{color: nameColor}}>{brand.name}</span>
                </a>
            );
        }
        const showName = logoStyle === 'with-name';
        return (
            <a href="/" className={`flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer min-w-0 no-underline ${className}`}>
                <div className={`relative shrink-0 ${wrapperClass}`} style={shapeStyle}>
                    {brand.logo_url && <img src={brand.logo_url} alt={brand.name} decoding="async" sizes="200px" className={`${hasShape ? imgClassShape : imgClassNormal} transition-opacity duration-500 ${dark ? 'opacity-0' : 'opacity-100'}`} style={hasShape ? undefined : shrinkStyle}/>}
                    {brand.logo_white_url && <img src={brand.logo_white_url} alt={brand.name} decoding="async" sizes="200px" className={`${hasShape ? imgClassShape + ' absolute inset-0' : imgClassNormal + ' absolute inset-0 m-auto'} transition-opacity duration-500 ${dark ? 'opacity-100' : 'opacity-0'}`} style={hasShape ? undefined : shrinkStyle}/>}
                    {!brand.logo_url && !brand.logo_white_url && (
                        hasShape
                            ? <div className="w-full h-full flex items-center justify-center"><span className="text-lg font-bold" style={{color: nameColor}}>{brand.name?.charAt(0)}</span></div>
                            : <span className="text-xl font-bold transition-colors duration-300 whitespace-nowrap" style={{color: nameColor}}>{brand.name}</span>
                    )}
                </div>
                {showName && (brand.logo_url || brand.logo_white_url) && (
                    <span className="text-lg font-semibold transition-colors truncate" style={{color: nameColor}}>{brand.name}</span>
                )}
            </a>
        );
    };

    // ── Mega-dropdown wrapper for nav items with children ──
    const NavItemDropdown = ({link, dark, children: navContent}) => {
        const [open, setOpen] = useState(false);
        const [activeTab, setActiveTab] = useState(0);
        const timeoutRef = useRef(null);
        const onEnter = () => { clearTimeout(timeoutRef.current); setOpen(true); };
        const onLeave = () => { timeoutRef.current = setTimeout(() => setOpen(false), 180); };
        if (!link.dropdown || !link.children || link.children.length === 0) return navContent;
        const ddStyle = link.dropdown_style || 'classic';
        const hasFeatured = link.featured && (link.featured.title || link.featured.image);
        const cols = ddStyle === 'visual' ? (link.children.length >= 3 ? 'grid-cols-3' : 'grid-cols-2')
            : link.children.length >= 4 ? 'grid-cols-2' : 'grid-cols-1';
        const panelWidth = ddStyle === 'tabbed' ? '560px' : ddStyle === 'visual' ? '540px' : (hasFeatured ? '560px' : (link.children.length >= 4 ? '480px' : '280px'));

        // Tabbed: group children by their "group" field or split evenly into tabs
        const tabbedGroups = ddStyle === 'tabbed' ? (() => {
            const groups = {};
            link.children.forEach(c => { const g = c.group || c.title; if (!groups[g]) groups[g] = []; groups[g].push(c); });
            return Object.entries(groups);
        })() : [];

        return (
            <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
                <button type="button" className="flex items-center gap-0.5 cursor-pointer bg-transparent border-none outline-none">
                    {navContent}
                    <svg className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''} ${dark ? 'text-white/50' : 'text-gray-400'}`} aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50 transition-all duration-200 ${open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}`}
                    style={{minWidth: panelWidth}}>
                    <div className={`rounded-2xl shadow-2xl border p-2 ${dark ? 'bg-gray-900/95 border-white/10' : 'bg-white border-gray-100'}`}
                        style={{backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)'}}>

                        {/* ── Tabbed mega menu ── */}
                        {ddStyle === 'tabbed' && tabbedGroups.length > 0 ? (
                            <div className="flex gap-0">
                                <div className={`flex flex-col gap-0.5 border-r py-1 pr-1 min-w-[140px] ${dark ? 'border-white/10' : 'border-gray-100'}`}>
                                    {tabbedGroups.map(([groupName], gi) => (
                                        <button key={gi} type="button" onMouseEnter={() => setActiveTab(gi)}
                                            className={`text-left px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${activeTab === gi
                                                ? (dark ? 'bg-white/15 text-white' : 'text-gray-900') : (dark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50')}`}
                                            style={activeTab === gi && !dark ? {backgroundColor: hexToRgba(primaryColor, 0.08), color: primaryColor} : {}}>
                                            {groupName}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex-1 p-1 min-w-[300px]">
                                    {tabbedGroups[activeTab] && tabbedGroups[activeTab][1].map((child, ci) => (
                                        <a key={ci} {...resolveLinkProps(child.link || '#')}
                                            {...(child.open_new_tab ? {target: '_blank', rel: 'noopener noreferrer'} : {})}
                                            className={`flex items-start gap-3 px-3 py-3 rounded-xl transition-all duration-150 group/dd ${dark ? 'hover:bg-white/10' : 'hover:bg-gray-50'}`}>
                                            {child.icon && <span className="mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-lg transition-transform group-hover/dd:scale-110" style={{backgroundColor: hexToRgba(primaryColor, 0.08)}}>{child.icon}</span>}
                                            {child.image && <img src={child.image} alt="" decoding="async" className="mt-0.5 w-9 h-9 rounded-xl object-cover shrink-0"/>}
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`text-sm font-semibold leading-tight ${dark ? 'text-white' : 'text-gray-900'}`}>{child.title}</span>
                                                    {child.badge && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{backgroundColor: hexToRgba(primaryColor, 0.12), color: primaryColor}}>{child.badge}</span>}
                                                </div>
                                                {child.description && <div className={`text-xs mt-0.5 leading-snug ${dark ? 'text-white/50' : 'text-gray-500'}`}>{child.description}</div>}
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            /* ── Classic / Visual mega menu ── */
                            <div className={hasFeatured ? 'flex gap-0' : ''}>
                                <div className={`${hasFeatured ? 'flex-1 pr-1' : ''}`}>
                                    <div className={`grid ${cols} gap-1`}>
                                        {link.children.map((child, ci) => (
                                            <a key={ci} {...resolveLinkProps(child.link || '#')}
                                                {...(child.open_new_tab ? {target: '_blank', rel: 'noopener noreferrer'} : {})}
                                                className={`flex ${ddStyle === 'visual' ? 'flex-col' : 'items-start'} gap-2 ${ddStyle === 'visual' ? 'p-2' : 'px-3 py-3'} rounded-xl transition-all duration-150 group/dd ${dark ? 'hover:bg-white/10' : 'hover:bg-gray-50'}`}>
                                                {ddStyle === 'visual' && child.image && (
                                                    <div className="w-full aspect-[16/10] rounded-lg overflow-hidden bg-gray-100">
                                                        <img src={child.image} alt="" decoding="async" className="w-full h-full object-cover transition-transform duration-300 group-hover/dd:scale-105"/>
                                                    </div>
                                                )}
                                                <div className={`flex ${ddStyle === 'visual' ? '' : 'items-start gap-3'}`}>
                                                    {child.icon && ddStyle !== 'visual' && (
                                                        <span className="mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-lg transition-transform group-hover/dd:scale-110"
                                                            style={{backgroundColor: hexToRgba(primaryColor, 0.08)}}>
                                                            {child.icon}
                                                        </span>
                                                    )}
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className={`text-sm font-semibold leading-tight ${dark ? 'text-white' : 'text-gray-900'}`}>{child.title}</span>
                                                            {child.badge && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{backgroundColor: hexToRgba(primaryColor, 0.12), color: primaryColor}}>{child.badge}</span>}
                                                        </div>
                                                        {child.description && <div className={`text-xs mt-0.5 leading-snug ${dark ? 'text-white/50' : 'text-gray-500'}`}>{child.description}</div>}
                                                    </div>
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                                {/* Featured section */}
                                {hasFeatured && (
                                    <div className={`w-48 border-l pl-2 py-1 flex flex-col ${dark ? 'border-white/10' : 'border-gray-100'}`}>
                                        {link.featured.image && (
                                            <a {...resolveLinkProps(link.featured.link || '#')} className="block rounded-xl overflow-hidden mb-2 group/ft">
                                                <img src={link.featured.image} alt="" decoding="async" className="w-full aspect-[4/3] object-cover transition-transform duration-300 group-hover/ft:scale-105"/>
                                            </a>
                                        )}
                                        {link.featured.title && <div className={`text-sm font-bold leading-tight ${dark ? 'text-white' : 'text-gray-900'}`}>{link.featured.title}</div>}
                                        {link.featured.description && <div className={`text-xs mt-1 leading-snug ${dark ? 'text-white/50' : 'text-gray-500'}`}>{link.featured.description}</div>}
                                        {link.featured.link && <a {...resolveLinkProps(link.featured.link)} className="text-xs font-semibold mt-2 inline-flex items-center gap-1 transition-colors hover:opacity-80" style={{color: primaryColor}}>Ver mas <span>→</span></a>}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const NavBadge = ({text}) => {
        if (!text) return null;
        return <span className="ml-1 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full leading-none" style={{backgroundColor: hexToRgba(primaryColor, 0.15), color: primaryColor}}>{text}</span>;
    };

    const NavItem = ({link, dark}) => {
        const accentColor = dark ? 'rgba(255,255,255,0.9)' : primaryColor;
        const isPage = !!link.href;
        const linkHref = isPage ? link.href : `#${link.id}`;
        const click = isPage ? undefined : e => smoothScroll(e, link.id);
        const extProps = link.external ? {target: '_blank', rel: 'noopener noreferrer'} : {};
        const isActive = activeHighlight && !isPage && activeSection === link.id;
        const sizeClass = navFontSize === 'xs' ? 'text-xs' : navFontSize === 'base' ? 'text-base' : 'text-sm';
        const weightClass = navFontWeight === 'normal' ? 'font-normal' : navFontWeight === 'semibold' ? 'font-semibold' : navFontWeight === 'bold' ? 'font-bold' : 'font-medium';
        const caseClass = navUppercase ? 'uppercase tracking-wider' : '';
        const baseClass = `${sizeClass} ${weightClass} ${caseClass} transition-all`;
        const activeStyle = isActive ? {color: accentColor} : {};
        const badge = link.badge;
        // Hover effect props
        const hoverProps = navHoverEffect === 'underline-slide' ? {
            style: {backgroundImage: `linear-gradient(${accentColor},${accentColor})`, backgroundSize: '0% 2px', backgroundRepeat: 'no-repeat', backgroundPosition: 'left bottom', transition: 'background-size 0.3s ease'},
            onMouseEnter: e => { e.currentTarget.style.backgroundSize = '100% 2px'; }, onMouseLeave: e => { e.currentTarget.style.backgroundSize = '0% 2px'; }
        } : navHoverEffect === 'bg-fill' ? {
            style: {backgroundImage: `linear-gradient(${accentColor},${accentColor})`, backgroundSize: '0% 100%', backgroundRepeat: 'no-repeat', backgroundPosition: 'left center', transition: 'background-size 0.3s ease, color 0.3s ease'},
            onMouseEnter: e => { e.currentTarget.style.backgroundSize = '100% 100%'; e.currentTarget.style.color = '#fff'; }, onMouseLeave: e => { e.currentTarget.style.backgroundSize = '0% 100%'; e.currentTarget.style.color = ''; }
        } : navHoverEffect === 'glow' ? {
            onMouseEnter: e => { e.currentTarget.style.textShadow = `0 0 8px ${accentColor}cc, 0 0 16px ${accentColor}66`; }, onMouseLeave: e => { e.currentTarget.style.textShadow = 'none'; }
        } : navHoverEffect === 'lift' ? {
            onMouseEnter: e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.filter = 'drop-shadow(0 4px 6px rgba(0,0,0,0.15))'; }, onMouseLeave: e => { e.currentTarget.style.transform = ''; e.currentTarget.style.filter = ''; }
        } : {};
        const hoverClass = navHoverEffect === 'scale' ? 'hover:scale-105 inline-block' : '';

        if (navStyle === 'underline') return (
            <a href={linkHref} onClick={click} {...extProps} className={`relative group px-3 py-2 ${baseClass} ${dark ? 'text-white/80 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`} style={activeStyle}>
                {link.label}<NavBadge text={badge}/>
                <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-300 ${isActive ? 'w-3/4' : 'w-0 group-hover:w-3/4'}`} style={{backgroundColor: accentColor}}/>
            </a>
        );
        if (navStyle === 'pill') return (
            <a href={linkHref} onClick={click} {...extProps}
                className={`px-4 py-1.5 rounded-full tracking-wide ${baseClass} ${isActive ? (dark ? 'text-white bg-white/25' : 'bg-gray-200 text-gray-900') : (dark ? 'text-white/90 bg-white/10 hover:bg-white/20' : 'text-gray-700 bg-gray-100 hover:bg-gray-200')}`}>
                {link.label}<NavBadge text={badge}/>
            </a>
        );
        if (navStyle === 'bold') return (
            <a href={linkHref} onClick={click} {...extProps}
                className={`py-2 px-3 uppercase tracking-[0.2em] text-[11px] font-extrabold transition-all ${dark ? 'text-white/80 hover:text-white' : 'text-gray-800 hover:text-black'}`} style={activeStyle}>
                {link.label}<NavBadge text={badge}/>
            </a>
        );
        if (navStyle === 'highlight') return (
            <a href={linkHref} onClick={click} {...extProps}
                className={`px-3 py-1.5 rounded-md ${baseClass} ${dark ? 'text-white/90 hover:bg-white/15' : 'text-gray-700 hover:text-gray-900'}`}
                style={!dark ? {backgroundColor: hexToRgba(primaryColor, isActive ? 0.15 : 0.06), ...(isActive ? {color: primaryColor} : {})} : activeStyle}>
                {link.label}<NavBadge text={badge}/>
            </a>
        );
        if (navStyle === 'dot') return (
            <a href={linkHref} onClick={click} {...extProps} className={`relative px-3 py-2 ${baseClass} group ${dark ? 'text-white/80 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`} style={activeStyle}>
                {link.label}<NavBadge text={badge}/>
                <span className={`absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full transition-opacity ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} style={{backgroundColor: accentColor}}/>
            </a>
        );
        if (navStyle === 'bordered') return (
            <a href={linkHref} onClick={click} {...extProps}
                className={`px-3 py-2 ${baseClass} border-b-2 ${dark ? 'text-white/80 border-white/30 hover:border-white hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                style={!dark ? {borderColor: isActive ? primaryColor : hexToRgba(primaryColor, 0.25), ...(isActive ? {color: primaryColor} : {})} : activeStyle}>
                {link.label}<NavBadge text={badge}/>
            </a>
        );
        if (navStyle === 'tag') return (
            <a href={linkHref} onClick={click} {...extProps}
                className={`px-3 py-1 rounded-lg text-xs font-semibold border ${baseClass} ${isActive ? (dark ? 'text-white border-white/50 bg-white/20' : `border-current bg-gray-50`) : (dark ? 'text-white/90 border-white/20 bg-white/5 hover:bg-white/15' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400 shadow-sm')}`}
                style={isActive && !dark ? {color: primaryColor, borderColor: primaryColor} : activeStyle}>
                {link.label}<NavBadge text={badge}/>
            </a>
        );
        // default
        return (
            <a href={linkHref} onClick={click} {...extProps} {...hoverProps}
                className={`px-3 py-2 rounded-lg ${baseClass} ${hoverClass} ${isActive ? (dark ? 'text-white bg-white/15' : 'text-gray-900 bg-gray-100') : (dark ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100')}`}
                style={{...(isActive ? activeStyle : {}), ...(hoverProps.style || {})}}>
                {link.label}<NavBadge text={badge}/>
            </a>
        );
    };

    const SepChar = ({dark}) => {
        if (navSeparator === 'none') return null;
        const ch = navSeparator === 'dot' ? '·' : navSeparator === 'line' ? '|' : navSeparator === 'slash' ? '/' : '—';
        return <span className={`text-xs select-none ${dark ? 'text-white/20' : 'text-gray-300'}`}>{ch}</span>;
    };

    const NavList = ({dark, className = ''}) => {
        if (!showNav) return null;
        return (
            <div className={`hidden md:flex items-center gap-1 ${navEntrance ? 'lm-nav-entrance' : ''} ${className}`} style={navFontFamily ? {fontFamily: `'${navFontFamily}', sans-serif`} : undefined}>
                {navLinks.map((link, i) => (
                    <React.Fragment key={link.id}>
                        {i > 0 && <SepChar dark={dark}/>}
                        <NavItemDropdown link={link} dark={dark}>
                            <NavItem link={link} dark={dark}/>
                        </NavItemDropdown>
                    </React.Fragment>
                ))}
            </div>
        );
    };

    // First "solid" or "pill" button shown on mobile inline
    const primaryBtn = buttons.find(b => (b.style || 'solid') === 'solid' || b.style === 'pill');

    const HeaderButtons = ({dark}) => {
        const hasContent = buttons.length > 0 || showSearch || stickyCtaText || smartCta || languages.length > 0 || showDarkToggle || socialProofText;
        if (!hasContent) return null;
        return (
            <div className="hidden md:flex items-center gap-2">
                <SocialProofTicker dark={dark}/>
                <SearchToggle dark={dark}/>
                <LanguageSwitcher dark={dark}/>
                <DarkModeToggle dark={dark}/>
                <StickyCtaBanner/>
                {buttons.map((btn, i) => {
                    const s = btn.style || 'solid';
                    const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                    const lp = resolveLinkProps(btn.link || '#');
                    const customBg = btn.bg_color;
                    const customText = btn.text_color;
                    let cls = 'text-sm px-5 py-2.5 font-semibold transition-all inline-flex items-center gap-2 hover:scale-105 ';
                    let style = {};
                    cls += s === 'pill' ? 'rounded-full ' : 'rounded-lg ';
                    if (s === 'solid' || s === 'pill') {
                        if (customBg) {
                            style.backgroundColor = customBg;
                            style.color = customText || (isColorDark(customBg) ? '#ffffff' : '#111827');
                            cls += 'shadow-lg hover:shadow-xl hover:opacity-90';
                        } else {
                            cls += dark ? 'bg-white text-gray-900 hover:bg-gray-100 shadow-lg hover:shadow-xl' : 'text-white shadow-lg hover:shadow-xl';
                            if (!dark) style.backgroundColor = primaryColor;
                        }
                    } else if (s === 'outline') {
                        if (customBg) {
                            style.color = customBg; style.borderColor = customBg;
                            cls += 'border-2 hover:opacity-80';
                        } else {
                            cls += dark ? 'text-white border-2 border-white/40 hover:bg-white/10' : 'border-2 hover:bg-gray-50';
                            if (!dark) { style.color = primaryColor; style.borderColor = primaryColor; }
                        }
                    } else { // ghost
                        if (customText) { style.color = customText; }
                        cls += dark ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100';
                    }
                    if (customText && (s === 'solid' || s === 'pill')) style.color = customText;
                    return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
                })}
            </div>
        );
    };

    const MobileButtons = () => {
        if (buttons.length === 0) return null;
        return (
            <div className="space-y-2 pt-4 mt-4 border-t border-gray-100">
                {buttons.map((btn, i) => {
                    const lp = resolveLinkProps(btn.link || '#');
                    const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                    const s = btn.style || 'solid';
                    const isPrimary = s === 'solid' || s === 'pill';
                    const customBg = btn.bg_color;
                    const customText = btn.text_color;
                    const bg = customBg || (isPrimary ? primaryColor : undefined);
                    const txt = customText || (isPrimary ? (bg && isColorDark(bg) ? '#ffffff' : '#111827') : undefined);
                    return (
                        <a key={i} {...lp} onClick={e => { lp.onClick?.(e); setMobileOpen(false); }}
                            className={`flex w-full items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${isPrimary ? '' : 'border border-gray-200 hover:bg-gray-50'}`}
                            style={isPrimary ? {backgroundColor: bg, color: txt} : (customText ? {color: customText} : {})}>
                            {icon}{btn.text}
                        </a>
                    );
                })}
            </div>
        );
    };

    // Search toggle
    const [searchOpen, setSearchOpen] = useState(false);
    const SearchToggle = ({dark}) => {
        if (!showSearch) return null;
        return (
            <div className="hidden md:flex items-center">
                {searchOpen ? (
                    <div className="flex items-center gap-2 animate-[fadeIn_0.2s_ease]">
                        <input type="text" placeholder="Buscar..." autoFocus onBlur={() => setSearchOpen(false)} aria-label="Buscar en el sitio"
                            className={`w-40 px-3 py-1.5 text-sm rounded-lg border outline-none transition-all ${dark ? 'bg-white/10 border-white/20 text-white placeholder-white/40 focus:border-white/40' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-400'}`}/>
                        <button onClick={() => setSearchOpen(false)} aria-label="Cerrar busqueda" className={`p-2 rounded ${dark ? 'text-white/50 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                            <svg className="w-4 h-4" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>
                ) : (
                    <button onClick={() => setSearchOpen(true)} aria-label="Abrir busqueda"
                        className={`p-2 rounded-lg transition-colors ${dark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
                        <svg className="w-4.5 h-4.5" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                    </button>
                )}
            </div>
        );
    };

    // Language switcher
    const [langOpen, setLangOpen] = useState(false);
    const LanguageSwitcher = ({dark}) => {
        if (languages.length === 0) return null;
        const current = languages[0];
        return (
            <div className="hidden md:block relative">
                <button onClick={() => setLangOpen(!langOpen)} aria-label="Seleccionar idioma" aria-expanded={langOpen} aria-haspopup="true"
                    className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${dark ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}>
                    <svg className="w-3.5 h-3.5" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9"/></svg>
                    {current.code || current}
                    <svg className={`w-2.5 h-2.5 transition-transform ${langOpen ? 'rotate-180' : ''}`} aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7"/></svg>
                </button>
                {langOpen && (
                    <div className={`absolute top-full right-0 mt-1 py-1 rounded-lg shadow-xl border min-w-[100px] z-50 ${dark ? 'bg-gray-900/95 border-white/10' : 'bg-white border-gray-100'}`}>
                        {languages.map((lang, li) => (
                            <a key={li} href={lang.url || `?lang=${lang.code || lang}`}
                                className={`block px-3 py-1.5 text-xs font-medium transition-colors ${li === 0 ? (dark ? 'text-white bg-white/10' : 'text-indigo-600 bg-indigo-50') : (dark ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')}`}>
                                {lang.label || lang.code || lang}
                            </a>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Sticky CTA — appears after scrolling past hero, supports smart CTA
    const StickyCtaBanner = () => {
        const useSmartText = smartCta && scrollProgress > (smartCta.threshold || 50);
        const ctaText = stickyCtaText || (useSmartText ? smartCta.text_after : smartCta?.text_before);
        const ctaLink = useSmartText ? (smartCta.link_after || stickyCtaLink) : (stickyCtaLink || smartCta?.link_before);
        if (!ctaText || !scrolled) return null;
        const pulseClass = ctaPulse ? 'lm-cta-pulse' : '';
        return (
            <a {...resolveLinkProps(ctaLink || '#contact')}
                className={`hidden md:inline-flex items-center gap-2 text-xs font-bold px-4 py-1.5 rounded-full text-white shadow-lg hover:scale-105 hover:shadow-xl transition-all animate-[fadeIn_0.3s_ease] ${pulseClass}`}
                style={{backgroundColor: primaryColor}}>
                {useSmartText ? smartCta.text_after : ctaText}
                <svg className="w-3 h-3" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7"/></svg>
            </a>
        );
    };

    // Dark mode toggle
    const DarkModeToggle = ({dark: isDark}) => {
        if (!showDarkToggle) return null;
        const toggle = () => document.querySelector('.lm-root')?.classList.toggle('lm-dark');
        return (
            <button onClick={toggle} aria-label="Cambiar modo oscuro/claro"
                className={`hidden md:flex p-2 rounded-lg transition-colors ${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
                <svg className="w-4 h-4" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
                </svg>
            </button>
        );
    };

    // Haptic feedback helper
    const haptic = useCallback((ms = 10) => { if (hapticFeedback && navigator.vibrate) navigator.vibrate(ms); }, [hapticFeedback]);

    // Social proof ticker
    const SocialProofTicker = ({dark: isDark}) => {
        if (!socialProofText) return null;
        return <span className={`hidden lg:inline-flex items-center gap-1.5 text-[10px] font-medium ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"/>
            {socialProofText}
        </span>;
    };

    // UTM-based CTA text override
    const [utmCtaOverride] = useState(() => {
        if (!utmCtaMap || typeof window === 'undefined') return null;
        const params = new URLSearchParams(window.location.search);
        const source = params.get('utm_source');
        return source && utmCtaMap[source] ? utmCtaMap[source] : null;
    });

    // Returning visitor detection
    const [isReturning] = useState(() => {
        if (typeof window === 'undefined') return false;
        const visited = localStorage.getItem('lm_visited');
        if (!visited) { localStorage.setItem('lm_visited', '1'); return false; }
        return true;
    });

    // Hover prefetch
    const prefetchOnHover = useCallback((e) => {
        try {
            if (window.self !== window.top) return;
            const href = e.currentTarget?.getAttribute('href');
            if (!href || href.startsWith('#') || href.startsWith('tel:') || href.startsWith('mailto:')) return;
            if (!document.querySelector(`link[href="${href}"]`)) {
                const link = document.createElement('link');
                link.rel = 'prefetch'; link.href = href;
                document.head.appendChild(link);
            }
        } catch (_) { /* cross-origin iframe — skip prefetch */ }
    }, []);

    const HamburgerBtn = ({dark, className = '', allScreens = false}) => {
        const hs = hamburgerStyle;
        const lineBase = `absolute h-0.5 w-5 rounded-full ${dark ? 'bg-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]' : 'bg-gray-600'}`;
        const dur = hs === 'spring' ? 'duration-500' : hs === 'squeeze' ? 'duration-300' : 'duration-300';
        const ease = hs === 'spring' ? 'cubic-bezier(0.34,1.56,0.64,1)' : hs === 'squeeze' ? 'cubic-bezier(0.23,1,0.32,1)' : undefined;
        const ts = ease ? {transition: `transform 300ms ${ease}, opacity 200ms ease`} : {};
        // Arrow open transforms
        const arrowOpen = hs === 'arrow';
        return (
            <button onClick={() => setMobileOpen(!mobileOpen)} aria-label="Abrir menu" aria-expanded={mobileOpen} aria-controls="mobile-menu"
                className={`${allScreens ? '' : 'md:hidden'} relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${dark ? 'text-white bg-white/15 hover:bg-white/25' : 'text-gray-600 hover:bg-gray-100'} ${className}`}>
                <span className="sr-only">Menu</span>
                <span className={`${lineBase} transition-all ${dur} ${mobileOpen ? (arrowOpen ? 'translate-x-[3px] translate-y-[3px] rotate-45 scale-x-[0.6]' : 'rotate-45') : '-translate-y-1.5'}`} style={mobileOpen && hs === 'squeeze' ? {transitionDelay: '100ms', ...ts} : ts}/>
                <span className={`${lineBase} transition-all ${dur} ${mobileOpen ? (arrowOpen ? 'translate-x-[3px] scale-x-[0.8]' : 'opacity-0 scale-0') : 'opacity-100'}`} style={ts}/>
                <span className={`${lineBase} transition-all ${dur} ${mobileOpen ? (arrowOpen ? 'translate-x-[3px] -translate-y-[3px] -rotate-45 scale-x-[0.6]' : '-rotate-45') : 'translate-y-1.5'}`} style={mobileOpen && hs === 'squeeze' ? {transitionDelay: '100ms', ...ts} : ts}/>
            </button>
        );
    };

    // ── Announcement Bar config ──
    const annStyle = hc.announcement_style || 'solid';
    const annIcon = hc.announcement_icon || '';
    const annBadge = hc.announcement_badge || '';
    const annCtaText = hc.announcement_cta_text || '';
    const annTextColor = hc.announcement_text_color || '#ffffff';
    const annGradient2 = hc.announcement_gradient2 || '';
    const annHeight = hc.announcement_height || 'normal';
    const annEntrance = hc.announcement_entrance || 'slide-down';
    const annAutoHide = hc.announcement_auto_hide || 0;
    const annTransition = hc.announcement_transition || 'fade';
    const annInterval = (hc.announcement_interval || 5) * 1000;
    const annPauseHover = hc.announcement_pause_hover !== false;
    const annCountdownDate = hc.announcement_countdown || '';
    const annPromoCode = hc.announcement_promo_code || '';
    const annPosition = hc.announcement_position || 'top';

    const allAnnouncements = announcementTexts.length > 0 ? announcementTexts : (announcementText ? [announcementText] : []);
    const [annIdx, setAnnIdx] = useState(0);
    const [annHovered, setAnnHovered] = useState(false);
    const [annVisible, setAnnVisible] = useState(false);
    const [promoCopied, setPromoCopied] = useState(false);

    // Entrance animation delay
    useEffect(() => {
        if (allAnnouncements.length === 0) return;
        const t = setTimeout(() => setAnnVisible(true), 100);
        return () => clearTimeout(t);
    }, []);

    // Auto-hide
    useEffect(() => {
        if (!annAutoHide || !annVisible || announcementClosed) return;
        const t = setTimeout(() => setAnnouncementClosed(true), annAutoHide * 1000);
        return () => clearTimeout(t);
    }, [annAutoHide, annVisible, announcementClosed]);

    // Rotation
    useEffect(() => {
        if (allAnnouncements.length <= 1 || announcementClosed || annHovered) return;
        const timer = setInterval(() => setAnnIdx(i => (i + 1) % allAnnouncements.length), annInterval);
        return () => clearInterval(timer);
    }, [allAnnouncements.length, announcementClosed, annHovered, annInterval]);

    // Countdown
    const [countdown, setCountdown] = useState('');
    useEffect(() => {
        if (!annCountdownDate) return;
        const update = () => {
            const diff = new Date(annCountdownDate) - new Date();
            if (diff <= 0) { setCountdown('Finalizado'); return; }
            const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000), m = Math.floor((diff % 3600000) / 60000), s = Math.floor((diff % 60000) / 1000);
            setCountdown(d > 0 ? `${d}d ${h}h ${m}m` : `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
        };
        update();
        const timer = setInterval(update, 1000);
        return () => clearInterval(timer);
    }, [annCountdownDate]);

    const copyPromo = () => {
        navigator.clipboard?.writeText(annPromoCode);
        setPromoCopied(true);
        setTimeout(() => setPromoCopied(false), 2000);
    };

    const AnnouncementBar = () => {
        if (allAnnouncements.length === 0 || announcementClosed) return null;
        const hPad = annHeight === 'compact' ? 'py-1' : annHeight === 'large' ? 'py-3' : 'py-2';
        const bgStyle = annStyle === 'gradient' && annGradient2
            ? {background: `linear-gradient(90deg, ${announcementBg}, ${annGradient2})`}
            : annStyle === 'glass'
            ? {backgroundColor: hexToRgba(announcementBg, 0.3), backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)'}
            : annStyle === 'dark'
            ? {backgroundColor: '#111827'}
            : {backgroundColor: announcementBg};
        const entranceClass = !annVisible ? (annEntrance === 'slide-down' ? '-translate-y-full' : annEntrance === 'fade' ? 'opacity-0' : '') : 'translate-y-0 opacity-100';
        const transClass = annTransition === 'slide' ? 'animate-[slideDown_0.3s_ease]' : annTransition === 'marquee' ? '' : 'animate-[fadeIn_0.4s_ease]';
        const isMarquee = annTransition === 'marquee' && allAnnouncements.length > 1;

        return (
            <div className={`flex items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm font-medium ${hPad} px-4 relative overflow-hidden transition-all duration-500 ${entranceClass}`}
                style={{...bgStyle, color: annTextColor}}
                onMouseEnter={annPauseHover ? () => setAnnHovered(true) : undefined}
                onMouseLeave={annPauseHover ? () => setAnnHovered(false) : undefined}>
                {/* Badge */}
                {annBadge && <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/20 shrink-0">{annBadge}</span>}
                {/* Icon */}
                {annIcon && <span className="text-sm sm:text-base shrink-0" aria-hidden="true">{annIcon}</span>}
                {/* Message — aria-live for screen readers */}
                <span aria-live="polite" aria-atomic="true" className="contents">
                {isMarquee ? (
                    <div className="overflow-hidden flex-1">
                        <div className="whitespace-nowrap animate-[marqueeScroll_20s_linear_infinite]">
                            {allAnnouncements.map((t, i) => <span key={i} className="mx-8">{t}</span>)}
                            {allAnnouncements.map((t, i) => <span key={`d-${i}`} className="mx-8">{t}</span>)}
                        </div>
                    </div>
                ) : (
                    <span key={annIdx} className={allAnnouncements.length > 1 ? transClass : ''}>{allAnnouncements[annIdx] || announcementText}</span>
                )}
                </span>
                {/* Countdown */}
                {countdown && <span className="font-mono text-xs sm:text-sm font-bold bg-white/15 px-2 py-0.5 rounded shrink-0">{countdown}</span>}
                {/* Promo code */}
                {annPromoCode && (
                    <button onClick={copyPromo} className="font-mono text-xs font-bold bg-white/20 hover:bg-white/30 px-2.5 py-0.5 rounded border border-white/30 transition-colors shrink-0 cursor-pointer">
                        {promoCopied ? 'Copiado!' : annPromoCode}
                    </button>
                )}
                {/* CTA link */}
                {announcementLink && (
                    <a {...resolveLinkProps(announcementLink)}
                        className="underline underline-offset-2 font-bold hover:opacity-80 transition-colors whitespace-nowrap shrink-0">
                        {annCtaText || 'Ver mas →'}
                    </a>
                )}
                {/* Close button */}
                {announcementDismissible && (
                    <button onClick={() => setAnnouncementClosed(true)} aria-label="Cerrar anuncio" className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-100 opacity-60 p-2 rounded transition-colors">
                        <svg className="w-3.5 h-3.5" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                )}
            </div>
        );
    };

    // ── Mobile nav item with accordion for children ──
    const MobileNavLink = ({link, close: closeFn, dark = false}) => {
        const [expanded, setExpanded] = useState(false);
        const hasChildren = link.dropdown && link.children && link.children.length > 0;
        const textCls = dark ? 'text-white/70 hover:text-white' : 'text-gray-700 hover:bg-gray-50';
        if (!hasChildren) {
            return (
                <a href={link.href || `#${link.id}`} onClick={link.href ? undefined : e => { smoothScroll(e, link.id); closeFn?.(); }}
                    className={`block px-4 py-3 rounded-lg font-medium transition-colors ${textCls}`}>{link.label}</a>
            );
        }
        return (
            <div>
                <button onClick={() => setExpanded(!expanded)} className={`flex w-full items-center justify-between px-4 py-3 rounded-lg font-medium transition-colors ${textCls}`}>
                    {link.label}
                    <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                </button>
                {expanded && (
                    <div className="pl-4 space-y-1 mt-1">
                        {link.children.map((child, ci) => (
                            <a key={ci} {...resolveLinkProps(child.link || '#')}
                                {...(child.open_new_tab ? {target:'_blank', rel:'noopener noreferrer'} : {})}
                                onClick={() => closeFn?.()} className={`flex items-start gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${dark ? 'text-white/50 hover:text-white/80' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
                                {child.icon && <span className="text-base mt-0.5">{child.icon}</span>}
                                <div>
                                    <span className="font-medium">{child.title}</span>
                                    {child.description && <span className={`block text-xs mt-0.5 ${dark ? 'text-white/30' : 'text-gray-400'}`}>{child.description}</span>}
                                </div>
                            </a>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const MobileMenu = () => {
        if (!mobileOpen) return null;
        const close = () => setMobileOpen(false);
        const animations = <style>{`
            @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
            @keyframes slideDown { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            @keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }
            @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @media(prefers-reduced-motion:reduce){*{animation-duration:0.01ms!important;transition-duration:0.01ms!important}}
            .animate-slideUp { animation: slideUp 0.3s ease-out; }
            .animate-slideDown { animation: slideDown 0.2s ease-out; }
            .animate-slideInLeft { animation: slideInLeft 0.3s ease-out; }
            .animate-slideInRight { animation: slideInRight 0.3s ease-out; }
            .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        `}</style>;

        // -- Fullscreen overlay --
        if (mobileStyle === 'fullscreen') return (
            <>{animations}
            <div ref={mobileMenuRef} role="dialog" aria-modal="true" aria-label="Menu de navegacion" className="fixed inset-0 z-[60] bg-gray-950/95 backdrop-blur-xl flex flex-col animate-fadeIn">
                <div className="flex items-center justify-between px-6 py-5">
                    <a href="/" className="flex items-center gap-3 no-underline hover:opacity-80 transition-opacity">
                        {brand.logo_white_url && <img src={brand.logo_white_url} alt={brand.name} decoding="async" sizes="200px" className="w-auto object-contain" style={{height: `${logoSizeMobilePx}px`}}/>}
                        {!brand.logo_white_url && <span className="text-xl font-bold text-white">{brand.name}</span>}
                    </a>
                    <button onClick={close} aria-label="Cerrar menu" className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors">
                        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center gap-2 px-6">
                    {navLinks.map((link, idx) => (
                        <MobileNavLink key={link.id} link={link} close={close} dark={true}/>
                    ))}
                    <MobileButtons/>
                </div>
                <div className="px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/40">
                    <div className="flex items-center gap-4">
                        {brand.contact_email && <a href={`mailto:${brand.contact_email}`} className="hover:text-white/70 transition-colors">{brand.contact_email}</a>}
                        {brand.contact_phone && <a href={`tel:${brand.contact_phone}`} className="hover:text-white/70 transition-colors">{brand.contact_phone}</a>}
                    </div>
                </div>
            </div>
            </>
        );

        // -- Bottom sheet --
        if (mobileStyle === 'bottom-sheet') return (
            <>{animations}
            <div role="dialog" aria-modal="true" aria-label="Menu de navegacion" className="fixed inset-0 z-[60] md:hidden">
                <div className="absolute inset-0 bg-black/50" onClick={close}/>
                <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-xl animate-slideUp max-h-[85vh] overflow-auto">
                    <div className="flex justify-center pt-3 pb-2"><div className="w-10 h-1 rounded-full bg-gray-300"/></div>
                    <div className="flex items-center justify-between px-6 pb-4 border-b border-gray-100">
                        <a href="/" className="font-semibold text-gray-900 no-underline hover:opacity-80 transition-opacity">{brand.name}</a>
                        <button onClick={close} aria-label="Cerrar menu" className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>
                    <nav aria-label="Menu principal" className="p-6 space-y-1">
                        {navLinks.map(link => (
                            <MobileNavLink key={link.id} link={link} close={close}/>
                        ))}
                        <MobileButtons/>
                    </nav>
                </div>
            </div>
            </>
        );

        // -- Dropdown (below header) --
        if (mobileStyle === 'dropdown') return (
            <>{animations}
            <div role="dialog" aria-modal="true" aria-label="Menu de navegacion" className="fixed top-0 left-0 right-0 z-[55] md:hidden" style={{paddingTop: height === 'compact' ? 48 : height === 'large' ? 80 : 64}}>
                <div className="fixed inset-0 bg-black/30 -z-10" onClick={close}/>
                <div className="bg-white shadow-xl border-t border-gray-100 animate-slideDown">
                    <nav aria-label="Menu principal" className={`${maxWidthClass} mx-auto ${paddingXClass} py-3 space-y-0.5`}>
                        {navLinks.map(link => (
                            <MobileNavLink key={link.id} link={link} close={close}/>
                        ))}
                        <MobileButtons/>
                    </nav>
                </div>
            </div>
            </>
        );

        // -- Drawer left --
        if (mobileStyle === 'drawer-left') return (
            <>{animations}
            <div role="dialog" aria-modal="true" aria-label="Menu de navegacion" className="fixed inset-0 z-[60] md:hidden">
                <div className="absolute inset-0 bg-black/50" onClick={close}/>
                <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl animate-slideInLeft">
                    <div className="flex items-center justify-between p-4 border-b border-gray-100">
                        <a href="/" className="font-semibold text-gray-900 no-underline hover:opacity-80 transition-opacity">{brand.name}</a>
                        <button onClick={close} aria-label="Cerrar menu" className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>
                    <nav aria-label="Menu principal" className="p-4 space-y-1">
                        {navLinks.map(link => (
                            <MobileNavLink key={link.id} link={link} close={close}/>
                        ))}
                        <MobileButtons/>
                    </nav>
                </div>
            </div>
            </>
        );

        // -- Iris reveal: circular expansion from hamburger --
        if (mobileStyle === 'iris-reveal') return (
            <>{animations}
            <style>{`
                @keyframes irisOpen { from { clip-path: circle(0% at calc(100% - 40px) 32px); } to { clip-path: circle(150% at calc(100% - 40px) 32px); } }
                .animate-iris { animation: irisOpen 0.5s ease-out forwards; }
            `}</style>
            <div role="dialog" aria-modal="true" aria-label="Menu de navegacion" className="fixed inset-0 z-[60] md:hidden animate-iris" style={{backgroundColor: primaryColor}}>
                <div className="flex items-center justify-between px-6 py-5">
                    <a href="/" className="flex items-center gap-3 no-underline hover:opacity-80 transition-opacity">
                        {brand.logo_white_url && <img src={brand.logo_white_url} alt={brand.name} decoding="async" sizes="200px" className="w-auto object-contain" style={{height: `${logoSizeMobilePx}px`}}/>}
                        {!brand.logo_white_url && <span className="text-xl font-bold text-white">{brand.name}</span>}
                    </a>
                    <button onClick={close} aria-label="Cerrar menu" className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors">
                        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center gap-2 px-6 -mt-16" style={{minHeight: 'calc(100vh - 160px)'}}>
                    {navLinks.map(link => (
                        <MobileNavLink key={link.id} link={link} close={close} dark={true}/>
                    ))}
                    <MobileButtons/>
                </div>
            </div>
            </>
        );

        // -- Push content: menu pushes page content to the side --
        if (mobileStyle === 'push-content') return (
            <>{animations}
            <style>{`
                @keyframes pushIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
                .animate-pushIn { animation: pushIn 0.35s ease-out; }
            `}</style>
            <div role="dialog" aria-modal="true" aria-label="Menu de navegacion" className="fixed inset-0 z-[60] md:hidden">
                <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={close}/>
                <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl animate-pushIn flex flex-col">
                    <div className="flex items-center justify-between p-5 border-b border-gray-100">
                        <a href="/" className="font-semibold text-gray-900 no-underline hover:opacity-80 transition-opacity">{brand.name}</a>
                        <button onClick={close} aria-label="Cerrar menu" className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>
                    <nav aria-label="Menu principal" className="flex-1 overflow-auto p-5 space-y-1">
                        {navLinks.map(link => (
                            <MobileNavLink key={link.id} link={link} close={close}/>
                        ))}
                        <MobileButtons/>
                    </nav>
                    <div className="p-5 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400">
                        {brand.contact_email && <a href={`mailto:${brand.contact_email}`} className="hover:text-gray-600 transition-colors">{brand.contact_email}</a>}
                        {brand.contact_phone && <a href={`tel:${brand.contact_phone}`} className="hover:text-gray-600 transition-colors">{brand.contact_phone}</a>}
                    </div>
                </div>
            </div>
            </>
        );

        // -- Staggered cards: each item appears as card with staggered animation --
        if (mobileStyle === 'staggered-cards') return (
            <>{animations}
            <style>{`
                @keyframes cardIn { from { opacity: 0; transform: translateY(20px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
                .stagger-card { animation: cardIn 0.3s ease-out forwards; opacity: 0; }
            `}</style>
            <div role="dialog" aria-modal="true" aria-label="Menu de navegacion" className="fixed inset-0 z-[60] md:hidden">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-md animate-fadeIn" onClick={close}/>
                <div className="absolute inset-x-4 top-20 bottom-4 flex flex-col items-center justify-center gap-3 overflow-auto">
                    {navLinks.map((link, idx) => (
                        <a key={link.id} href={link.href || `#${link.id}`} onClick={link.href ? () => close() : e => { smoothScroll(e, link.id); close(); }}
                            className="stagger-card w-full max-w-xs bg-white rounded-2xl shadow-lg px-6 py-4 text-center font-semibold text-gray-800 hover:shadow-xl transition-shadow"
                            style={{animationDelay: `${idx * 80}ms`}}>
                            {link.label}
                            {link.badge && <span className="ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{backgroundColor: hexToRgba(primaryColor, 0.12), color: primaryColor}}>{link.badge}</span>}
                        </a>
                    ))}
                    <div className="stagger-card w-full max-w-xs" style={{animationDelay: `${navLinks.length * 80}ms`}}>
                        <MobileButtons/>
                    </div>
                    <button onClick={close} className="stagger-card mt-2 p-3 bg-white/90 rounded-full shadow-lg text-gray-400 hover:text-gray-600" style={{animationDelay: `${(navLinks.length + 1) * 80}ms`}}>
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                </div>
            </div>
            </>
        );

        // -- Default: drawer-right --
        return (
            <>{animations}
            <div ref={mobileMenuRef} role="dialog" aria-modal="true" aria-label="Menu de navegacion" className="fixed inset-0 z-[60] md:hidden">
                <div className="absolute inset-0 bg-black/50" onClick={close}/>
                <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-xl animate-slideInRight">
                    <div className="flex items-center justify-between p-4 border-b border-gray-100">
                        <a href="/" className="font-semibold text-gray-900 no-underline hover:opacity-80 transition-opacity">{brand.name}</a>
                        <button onClick={close} aria-label="Cerrar menu" className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>
                    <nav aria-label="Menu principal" className="p-4 space-y-1">
                        {navLinks.map(link => (
                            <MobileNavLink key={link.id} link={link} close={close}/>
                        ))}
                        <MobileButtons/>
                    </nav>
                </div>
            </div>
            </>
        );
    };

    const ProgressBar = () => {
        if (!showProgressBar) return null;
        return <div className="absolute bottom-0 left-0 h-0.5 transition-all duration-100" style={{width: `${scrollProgress}%`, backgroundColor: primaryColor}} />;
    };

    const BorderLine = () => {
        if (gradientBorder) return <div className="absolute bottom-0 left-0 right-0 lm-grad-border"/>;
        if (borderStyle === 'none') return null;
        if (borderStyle === 'gradient') return <div className="absolute bottom-0 left-0 right-0 h-1" style={{background: `linear-gradient(to right, ${primaryColor}, transparent)`}}/>;
        if (borderStyle === 'glow') return <div className="absolute bottom-0 left-0 right-0 h-1 rounded-full" style={{backgroundColor: primaryColor, boxShadow: `0 2px 12px ${colorVars.a25}, 0 0 4px ${primaryColor}`}}/>;
        return <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{backgroundColor: colorVars.a25}}/>;
    };

    const headerVisibilityClass = scrollBehavior === 'hide-show'
        ? (headerVisible ? 'translate-y-0' : '-translate-y-full')
        : '';
    const isShrunk = scrollBehavior === 'shrink' && (startsOpaque ? scrollY > 60 : scrolled);
    const shrinkClass = isShrunk ? 'py-0' : '';
    const shrinkStyle = isShrunk && shrinkScale < 100 ? { transform: `scaleY(${shrinkScale / 100})`, transformOrigin: 'top' } : {};

    // Nav background classes — unified for all variants that use the standard pattern
    const blurScrollClass = blurOnScroll && scrolled ? 'backdrop-blur-xl' : '';
    const navBgClass = scrolled
        ? `${hasShadow ? 'shadow-md' : ''} ${currentBlur || blurOnScroll ? 'backdrop-blur-md' : ''} ${blurScrollClass}`
        : initialStyle === 'blur' ? 'backdrop-blur-xl' : (initialStyle === 'transparent' && !initialBg ? 'backdrop-blur-[2px]' : '');

    // Shared CSS for all variants (logo sizing, animations, etc.)
    // Rendered inline in each variant's return via {sharedCss}
    const sharedCss = <HeaderCss/>;

    // -- Glass --
    if (variant === 'glass') {
        const glassBg = initialBg ? hexToRgba(initialBg, 0.15) : 'rgba(255,255,255,0.1)';
        const dark = scrolled ? scrolledIsDark : (initialBg ? isColorDark(initialBg) : true);
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`${positionClass} top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-white/10 transition-all duration-300 ${scrolled && hasShadow ? 'shadow-md' : 'shadow-sm'} ${headerVisibilityClass}`}
                    style={{backgroundColor: scrolled ? bgScrolled : glassBg, ...transitionStyle, ...radiusStyle}}>
                    <AnnouncementBar/>
                    <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                        <div className={`flex justify-between items-center ${hClass} ${shrinkClass}`}>
                            <LogoBlock dark={dark}/>
                            <NavList dark={dark}/>
                            <div className="flex items-center gap-3 shrink-0">
                                <PhoneLink cls={dark ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-700'}/>
                                <HeaderButtons dark={dark}/>
                                <HamburgerBtn dark={dark}/>
                            </div>
                        </div>
                    </div>
                    <ProgressBar/>
                    <BorderLine/>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Pill --
    if (variant === 'pill') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`${positionClass} left-4 right-4 z-50 transition-all duration-300 ${scrolled ? 'top-2' : 'top-4'} ${headerVisibilityClass}`}>
                    <AnnouncementBar/>
                    <div className={`max-w-6xl mx-auto rounded-2xl ${scrolled ? 'shadow-xl' : 'bg-white/10 backdrop-blur-xl border border-white/20'} transition-all duration-300 px-4 sm:px-6`}
                        style={{backgroundColor: scrolled ? bgScrolled : (initialBg || undefined), ...transitionStyle, ...radiusStyle}}>
                        <div className={`flex justify-between items-center ${hClass} ${shrinkClass}`}>
                            <LogoBlock dark={dark}/>
                            <NavList dark={dark}/>
                            <div className="flex items-center gap-3 shrink-0">
                                <PhoneLink cls={`hidden md:inline-flex ${dark ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}/>
                                <HeaderButtons dark={dark}/>
                                <HamburgerBtn dark={dark}/>
                            </div>
                        </div>
                    </div>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Minimal --
    if (variant === 'minimal') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`${positionClass} top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass} ${headerVisibilityClass}`}
                    style={{ backgroundColor: currentBgStyle, ...transitionStyle, ...radiusStyle }}>
                    <AnnouncementBar/>
                    <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                        <div className={`flex justify-between items-center ${hClass} ${shrinkClass}`}>
                            <LogoBlock dark={dark}/>
                            <div className="flex items-center gap-3 shrink-0">
                                <PhoneLink cls={`hidden md:inline-flex ${dark ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}/>
                                <HeaderButtons dark={dark}/>
                                <HamburgerBtn dark={dark}/>
                            </div>
                        </div>
                    </div>
                    <ProgressBar/>
                    <BorderLine/>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Centered --
    if (variant === 'centered') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`${positionClass} top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass} ${headerVisibilityClass}`}
                    style={{ backgroundColor: currentBgStyle, ...transitionStyle, ...radiusStyle }}>
                    <AnnouncementBar/>
                    <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                        {/* Mobile: row layout with logo left, hamburger right */}
                        <div className={`flex md:hidden justify-between items-center ${hClass} ${shrinkClass}`}>
                            <LogoBlock dark={dark}/>
                            <div className="flex items-center gap-2 shrink-0">
                                <HamburgerBtn dark={dark}/>
                            </div>
                        </div>
                        {/* Desktop: centered layout */}
                        <div className={`hidden md:flex flex-col items-center ${height === 'compact' ? 'py-2' : 'py-3'} ${shrinkClass}`}>
                            <LogoBlock dark={dark} className="mb-1"/>
                            {showNav && navLinks.length > 0 && (
                                <div className="flex items-center gap-1">
                                    {navLinks.map(link => (
                                        <NavItemDropdown key={link.id} link={link} dark={dark}>
                                            <NavItem link={link} dark={dark}/>
                                        </NavItemDropdown>
                                    ))}
                                    <PhoneLink cls={dark ? 'text-white/70 hover:text-white ml-3' : 'text-gray-500 hover:text-gray-700 ml-3'}/>
                                    <div className="ml-3"><HeaderButtons dark={dark}/></div>
                                </div>
                            )}
                            {(!showNav || navLinks.length === 0) && <HeaderButtons dark={dark}/>}
                        </div>
                    </div>
                    <ProgressBar/>
                    <BorderLine/>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Topbar: two-row header with colored strip --
    if (variant === 'topbar') {
        return (
            <>{sharedCss}
                <div className={`${positionClass} top-0 left-0 right-0 z-50 transition-all duration-300 ${headerVisibilityClass}`}>
                    {/* Top colored strip — marquee auto-scroll when content overflows */}
                    {(() => {
                        const emailIcon = <svg className="w-3 h-3 shrink-0" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>;
                        const phoneIcon = <svg className="w-3 h-3 shrink-0" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>;
                        const socials = brand.social_links || {};
                        const socialNames = {facebook:'Facebook',instagram:'Instagram',twitter:'Twitter',youtube:'YouTube',linkedin:'LinkedIn',tiktok:'TikTok'};
                        const addressIcon = <svg className="w-3 h-3 shrink-0" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>;
                        const topbarBg = hc.topbar_bg || primaryColor;
                        const topbarTextColor = hc.topbar_text_color || '#ffffff';
                        const topbarSpeed = hc.topbar_speed === 'fast' ? '12s' : hc.topbar_speed === 'slow' ? '40s' : '25s';
                        const hideOnScroll = hc.topbar_hide_scroll !== false;
                        const linkClass = `inline-flex items-center gap-1 hover:opacity-70 transition-opacity whitespace-nowrap`;
                        const topbarItems = [];
                        if (hc.topbar_show_email !== false && brand.contact_email) topbarItems.push(<a key="email" href={`mailto:${brand.contact_email}`} className={linkClass}>{emailIcon}{brand.contact_email}</a>);
                        if (hc.topbar_show_phone !== false && brand.contact_phone) topbarItems.push(<a key="phone" href={`tel:${brand.contact_phone}`} className={linkClass}>{phoneIcon}{brand.contact_phone}</a>);
                        if (hc.topbar_show_address && brand.address) topbarItems.push(<span key="address" className={linkClass}>{addressIcon}{brand.address}</span>);
                        if (hc.topbar_custom_text) topbarItems.push(<span key="custom" className="whitespace-nowrap">{hc.topbar_custom_text}</span>);
                        if (hc.topbar_show_social !== false) {
                            Object.entries(socialNames).forEach(([k,v]) => {
                                if (socials[k]) topbarItems.push(<a key={k} href={socials[k]} target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-opacity whitespace-nowrap">{v}</a>);
                            });
                        }
                        if (topbarItems.length === 0) return null;
                        const sep = <span style={{opacity: 0.3}} className="mx-1">·</span>;
                        const ItemRow = () => <>{topbarItems.map((item, i) => <React.Fragment key={i}>{i > 0 && sep}{item}</React.Fragment>)}</>;
                        const topbarH = hc.topbar_height === 'compact' ? 'h-6' : hc.topbar_height === 'tall' ? 'h-10' : hc.topbar_height === 'extra' ? 'h-14' : 'h-8';
                        const topbarMaxH = hc.topbar_height === 'compact' ? 'max-h-6' : hc.topbar_height === 'tall' ? 'max-h-10' : hc.topbar_height === 'extra' ? 'max-h-14' : 'max-h-10';
                        const topbarAnim = hc.topbar_anim || 'scroll';
                        const isGradientBg = topbarAnim === 'gradient-bg';
                        const topbarBgStyle = isGradientBg
                            ? { background: `linear-gradient(270deg, ${topbarBg}, ${hc.topbar_text_color || '#ffffff'}, ${topbarBg})`, backgroundSize: '200% 200%', animation: `gradientShift 4s ease infinite`, color: topbarTextColor }
                            : { backgroundColor: topbarBg, color: topbarTextColor };
                        const fadeEdges = topbarAnim === 'scroll-fade' ? { WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' } : {};
                        const animName = topbarAnim === 'bounce' ? 'marqueeBounce' : 'marqueeScroll';
                        const animDir = topbarAnim === 'scroll-reverse' ? 'reverse' : 'normal';
                        const animStyle = topbarAnim === 'static' ? {} : { animation: `${animName} ${topbarSpeed} linear infinite`, animationDirection: animDir, willChange: 'transform' };
                        return (
                            <div className={`overflow-hidden transition-all duration-300 ${hideOnScroll && scrolled ? 'max-h-0' : topbarMaxH}`}
                                style={topbarBgStyle}>
                                <div className={`${topbarH} flex items-center overflow-hidden ${hc.topbar_font_size === '2xs' ? 'text-[10px]' : hc.topbar_font_size === 'sm' ? 'text-sm' : hc.topbar_font_size === 'base' ? 'text-base' : 'text-xs'}`}
                                    style={fadeEdges}>
                                    {topbarAnim === 'static' ? (
                                        <div className="flex items-center justify-center gap-5 w-full px-4 whitespace-nowrap">
                                            <ItemRow/>
                                        </div>
                                    ) : (
                                        <div className="lm-topbar-scroll inline-flex items-center whitespace-nowrap" style={animStyle}>
                                            <div className="inline-flex items-center gap-5 shrink-0 px-8"><ItemRow/></div>
                                            <div className="inline-flex items-center gap-5 shrink-0 px-8" aria-hidden="true"><ItemRow/></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                    {/* Main nav row */}
                    <nav aria-label="Menu principal" className={`transition-shadow duration-300 ${scrolled && hasShadow ? 'shadow-md' : ''} relative`}
                        style={{backgroundColor: bgScrolled, ...transitionStyle, ...radiusStyle}}>
                        <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                            <div className={`flex justify-between items-center ${hClass} ${shrinkClass}`}>
                                <LogoBlock dark={scrolledIsDark}/>
                                <NavList dark={scrolledIsDark}/>
                                <div className="flex items-center gap-3 shrink-0">
                                    <PhoneLink cls={`hidden md:inline-flex ${scrolledIsDark ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}/>
                                    <HeaderButtons dark={scrolledIsDark}/>
                                    <HamburgerBtn dark={scrolledIsDark}/>
                                </div>
                            </div>
                        </div>
                        <ProgressBar/>
                        <BorderLine/>
                    </nav>
                </div>
                <MobileMenu/>
            </>
        );
    }

    // -- Split Nav: centered logo with split navigation --
    if (variant === 'split-nav') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        const leftLinks = navLinks.slice(0, Math.ceil(navLinks.length / 2));
        const rightLinks = navLinks.slice(Math.ceil(navLinks.length / 2));
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`${positionClass} top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass} ${headerVisibilityClass}`}
                    style={{ backgroundColor: currentBgStyle, ...transitionStyle, ...radiusStyle }}>
                    <AnnouncementBar/>
                    <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                        <div className={`flex justify-between items-center ${hClass} ${shrinkClass}`}>
                            {/* Left nav links */}
                            {showNav && (
                                <div className="hidden md:flex items-center gap-0.5 flex-1 justify-end">
                                    {leftLinks.map(link => (
                                        <NavItemDropdown key={link.id} link={link} dark={dark}>
                                            <NavItem link={link} dark={dark}/>
                                        </NavItemDropdown>
                                    ))}
                                </div>
                            )}
                            {/* Centered logo */}
                            <LogoBlock dark={dark} className="mx-6 shrink-0 justify-center"/>
                            {/* Right nav links + login */}
                            <div className="hidden md:flex items-center gap-0.5 flex-1">
                                {showNav && rightLinks.map(link => (
                                    <NavItemDropdown key={link.id} link={link} dark={dark}>
                                        <NavItem link={link} dark={dark}/>
                                    </NavItemDropdown>
                                ))}
                                <div className="flex items-center gap-3 ml-auto">
                                    <PhoneLink cls={`${dark ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}/>
                                    <HeaderButtons dark={dark}/>
                                </div>
                            </div>
                            {/* Mobile hamburger */}
                            <HamburgerBtn dark={dark}/>
                        </div>
                    </div>
                    <ProgressBar/>
                    <BorderLine/>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Fullscreen Menu: hamburger-only with fullscreen overlay --
    if (variant === 'fullscreen-menu') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`${positionClass} top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass} ${headerVisibilityClass}`}
                    style={{ backgroundColor: currentBgStyle, ...transitionStyle, ...radiusStyle }}>
                    <AnnouncementBar/>
                    <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                        <div className={`flex justify-between items-center ${hClass} ${shrinkClass}`}>
                            <LogoBlock dark={dark}/>
                            <div className="flex items-center gap-3 shrink-0">
                                <HeaderButtons dark={dark}/>
                                <HamburgerBtn dark={dark} allScreens={true}/>
                            </div>
                        </div>
                    </div>
                    <ProgressBar/>
                    <BorderLine/>
                </nav>
                {/* Fullscreen overlay - the variant's unique feature */}
                {mobileOpen && (
                    <div id="mobile-menu" className="fixed inset-0 z-[60] bg-gray-950/95 backdrop-blur-xl flex flex-col">
                        {/* Top bar: logo + close */}
                        <div className="flex items-center justify-between px-6 py-5">
                            <div className="flex items-center gap-3">
                                {brand.logo_white_url && <img src={brand.logo_white_url} alt={brand.name} decoding="async" sizes="200px" className="w-auto object-contain" style={{height: `${logoSizeMobilePx}px`}}/>}
                                {!brand.logo_white_url && <span className="text-xl font-bold text-white">{brand.name}</span>}
                            </div>
                            <button onClick={() => setMobileOpen(false)} className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors">
                                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12"/></svg>
                            </button>
                        </div>
                        {/* Centered nav links */}
                        <div className="flex-1 flex flex-col items-center justify-center gap-2 px-6">
                            {navLinks.map((link, idx) => (
                                <a key={link.id} href={link.href || `#${link.id}`} onClick={link.href ? undefined : e => smoothScroll(e, link.id)}
                                    className="text-3xl md:text-5xl font-light text-white/70 hover:text-white transition-all duration-300 hover:translate-x-3 py-2"
                                    style={{ transitionDelay: `${idx * 60}ms` }}>
                                    {link.label}
                                </a>
                            ))}
                            <MobileButtons/>
                        </div>
                        {/* Bottom: contact + social */}
                        <div className="px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/40">
                            <div className="flex items-center gap-4">
                                {brand.contact_email && (
                                    <a href={`mailto:${brand.contact_email}`} className="hover:text-white/70 transition-colors">{brand.contact_email}</a>
                                )}
                                {brand.contact_phone && (
                                    <a href={`tel:${brand.contact_phone}`} className="hover:text-white/70 transition-colors">{brand.contact_phone}</a>
                                )}
                            </div>
                            <div className="flex items-center gap-4">
                                {brand.social_links?.facebook && <a href={brand.social_links.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">Facebook</a>}
                                {brand.social_links?.instagram && <a href={brand.social_links.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">Instagram</a>}
                                {brand.social_links?.twitter && <a href={brand.social_links.twitter} target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">Twitter</a>}
                                {brand.social_links?.youtube && <a href={brand.social_links.youtube} target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">YouTube</a>}
                                {brand.social_links?.linkedin && <a href={brand.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">LinkedIn</a>}
                                {brand.social_links?.tiktok && <a href={brand.social_links.tiktok} target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">TikTok</a>}
                            </div>
                        </div>
                    </div>
                )}
                {/* Static preview of nav links for editor (overlay can't be opened interactively in preview) */}
                {!mobileOpen && !enableAnimations && navLinks.length > 0 && (
                    <div className="bg-gray-950/95 backdrop-blur-xl py-8 px-6">
                        <div className="flex flex-col items-center gap-1">
                            {navLinks.map((link, idx) => (
                                <a key={link.id} href={link.href || `#${link.id}`}
                                    className="text-xl font-light text-white/70 hover:text-white transition-all duration-300 hover:translate-x-2 py-1.5">
                                    {link.label}
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </>
        );
    }

    // -- Floating Island: detached card with gap from edges --
    if (variant === 'floating-island') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        return (
            <>{sharedCss}
                <AnnouncementBar/>
                <nav aria-label="Menu principal" className={`${positionClass} left-0 right-0 z-50 transition-all duration-500 ${headerVisibilityClass}`}
                    style={{ top: announcementText && !announcementClosed ? '2.5rem' : '0.75rem', ...transitionStyle }}>
                    <div className="max-w-5xl mx-auto px-4">
                        <div className={`rounded-2xl transition-all duration-500 px-4 sm:px-6 ${
                            scrolled ? 'shadow-2xl' : 'shadow-lg backdrop-blur-xl'
                        }`} style={{ backgroundColor: scrolled ? bgScrolled : hexToRgba(bgScrolled || '#ffffff', 0.85), ...radiusStyle }}>
                            <div className={`flex justify-between items-center ${hClass} ${shrinkClass}`}>
                                <LogoBlock dark={dark}/>
                                <NavList dark={dark}/>
                                <div className="flex items-center gap-3 shrink-0">
                                    <HeaderButtons dark={dark}/>
                                    <HamburgerBtn dark={dark}/>
                                </div>
                            </div>
                            <ProgressBar/>
                            <BorderLine/>
                        </div>
                    </div>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Dual Bar: utility bar (always visible) + main nav bar --
    if (variant === 'dual-bar') {
        return (
            <>{sharedCss}
                <div className={`${positionClass} top-0 left-0 right-0 z-50 transition-all duration-300 ${headerVisibilityClass}`}>
                    {/* Utility bar — always visible */}
                    <div style={{ backgroundColor: primaryColor }} className="text-white">
                        <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                            <div className="flex justify-between items-center h-8 text-xs">
                                <div className="flex items-center gap-4">
                                    {brand.contact_email && (
                                        <a href={`mailto:${brand.contact_email}`} className="inline-flex items-center gap-1 hover:text-white/80 transition-colors">
                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                                            {brand.contact_email}
                                        </a>
                                    )}
                                    {brand.contact_phone && (
                                        <a href={`tel:${brand.contact_phone}`} className="inline-flex items-center gap-1 hover:text-white/80 transition-colors">
                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                                            {brand.contact_phone}
                                        </a>
                                    )}
                                </div>
                                <div className="hidden sm:flex items-center gap-3">
                                    {brand.social_links?.facebook && <a href={brand.social_links.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition-colors">Facebook</a>}
                                    {brand.social_links?.instagram && <a href={brand.social_links.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition-colors">Instagram</a>}
                                    {brand.social_links?.twitter && <a href={brand.social_links.twitter} target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition-colors">Twitter</a>}
                                    {brand.social_links?.linkedin && <a href={brand.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition-colors">LinkedIn</a>}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Main nav row */}
                    <nav aria-label="Menu principal" className={`transition-shadow duration-300 ${scrolled && hasShadow ? 'shadow-md' : ''}`}
                        style={{ backgroundColor: bgScrolled, ...transitionStyle, ...radiusStyle }}>
                        <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                            <div className={`flex justify-between items-center ${hClass} ${shrinkClass}`}>
                                <LogoBlock dark={scrolledIsDark}/>
                                <NavList dark={scrolledIsDark}/>
                                <div className="flex items-center gap-3 shrink-0">
                                    <HeaderButtons dark={scrolledIsDark}/>
                                    <HamburgerBtn dark={scrolledIsDark}/>
                                </div>
                            </div>
                        </div>
                        <ProgressBar/>
                        <BorderLine/>
                    </nav>
                </div>
                <MobileMenu/>
            </>
        );
    }

    // -- Stacked Centered: logo row + nav row --
    if (variant === 'stacked-centered') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`${positionClass} top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass} ${headerVisibilityClass}`}
                    style={{ backgroundColor: currentBgStyle, ...transitionStyle, ...radiusStyle }}>
                    <AnnouncementBar/>
                    <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                        {/* Row 1: logo centered (desktop only) */}
                        <div className="hidden md:flex justify-center items-center pt-4 pb-1">
                            <LogoBlock dark={dark} className="!mr-0"/>
                        </div>
                        {/* Row 2: nav centered (desktop only) */}
                        <div className="hidden md:flex justify-center items-center pb-3 gap-4">
                            <NavList dark={dark} className="!hidden md:!flex"/>
                            <HeaderButtons dark={dark}/>
                        </div>
                        {/* Mobile: single row */}
                        <div className={`md:hidden flex justify-between items-center ${hClass} ${shrinkClass}`}>
                            <LogoBlock dark={dark}/>
                            <div className="flex items-center gap-2 shrink-0">
                                <HeaderButtons dark={dark}/>
                                <HamburgerBtn dark={dark}/>
                            </div>
                        </div>
                    </div>
                    <ProgressBar/>
                    <BorderLine/>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Editorial Band: typography-first, hard border, no blur/shadow --
    if (variant === 'editorial-band') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`${positionClass} top-0 left-0 right-0 z-50 border-b-2 transition-all duration-300 ${headerVisibilityClass}`}
                    style={{ backgroundColor: currentBgStyle, borderColor: dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)', ...transitionStyle }}>
                    <AnnouncementBar/>
                    <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                        <div className={`flex justify-between items-center ${hClass} ${shrinkClass}`}>
                            <div className="flex items-center gap-2 shrink-0">
                                {showLogo && brand.logo_url && !dark && <img src={brand.logo_url} alt={brand.name} decoding="async" sizes="200px" className="h-8 w-auto object-contain"/>}
                                {showLogo && brand.logo_white_url && dark && <img src={brand.logo_white_url} alt={brand.name} decoding="async" sizes="200px" className="h-8 w-auto object-contain"/>}
                                {showLogo && <span className={`text-2xl font-bold tracking-tight ${dark ? 'text-white' : 'text-gray-900'}`}>{brand.name}</span>}
                            </div>
                            <NavList dark={dark}/>
                            <div className="flex items-center gap-3 shrink-0">
                                <HeaderButtons dark={dark}/>
                                <HamburgerBtn dark={dark}/>
                            </div>
                        </div>
                    </div>
                    <ProgressBar/>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Scroll Reveal: hidden initially, appears after scrolling --
    if (variant === 'scroll-reveal') {
        const revealed = scrolled;
        const showInPreview = !enableAnimations;
        const isVisible = revealed || showInPreview;
        const dark = isVisible ? scrolledIsDark : true;
        const revealBg = isVisible ? bgScrolled : (currentBgStyle || 'transparent');
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    isVisible ? 'translate-y-0 opacity-100 shadow-lg' : '-translate-y-full opacity-0'
                }`} style={{ backgroundColor: revealBg, ...transitionStyle, ...radiusStyle }}>
                    <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                        <div className={`flex justify-between items-center ${hClass}`}>
                            <LogoBlock dark={dark}/>
                            <NavList dark={dark}/>
                            <div className="flex items-center gap-3 shrink-0">
                                <PhoneLink cls={`hidden md:inline-flex ${dark ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}/>
                                <HeaderButtons dark={dark}/>
                                <HamburgerBtn dark={dark}/>
                            </div>
                        </div>
                    </div>
                    <ProgressBar/>
                    <BorderLine/>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Drawer Overlay: minimal — logo + hamburger only, everything via mobile menu --
    if (variant === 'drawer-overlay') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`${positionClass} top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass} ${headerVisibilityClass}`}
                    style={{ backgroundColor: currentBgStyle, ...transitionStyle, ...radiusStyle }}>
                    <AnnouncementBar/>
                    <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                        <div className={`flex justify-between items-center ${hClass} ${shrinkClass}`}>
                            <LogoBlock dark={dark}/>
                            <div className="flex items-center gap-3 shrink-0">
                                <HeaderButtons dark={dark}/>
                                <HamburgerBtn dark={dark} allScreens={true}/>
                            </div>
                        </div>
                    </div>
                    <ProgressBar/>
                    <BorderLine/>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Bottom Bar: fixed at bottom of viewport --
    if (variant === 'bottom-bar') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        const bottomBg = scrolled
            ? bgScrolled
            : (initialBg ? hexToRgba(initialBg, initialOpacity < 1 ? Math.max(initialOpacity, 0.7) : 1) : hexToRgba(bgScrolled || '#ffffff', initialOpacity < 1 ? Math.max(initialOpacity, 0.7) : 1));
        const bottomRadius = headerRadius > 0 ? (headerRadius >= 9999 ? '9999px' : `${headerRadius}px`) : '9999px';
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`fixed bottom-3 left-4 right-4 z-50 transition-all duration-300`}
                    style={transitionStyle}>
                    <div className="max-w-3xl mx-auto">
                        <div className={`shadow-2xl backdrop-blur-xl px-4 sm:px-6 transition-all duration-300`}
                            style={{ backgroundColor: bottomBg, borderRadius: bottomRadius }}>
                            <div className="flex justify-between items-center h-14">
                                <LogoBlock dark={dark} className="!mr-0"/>
                                <NavList dark={dark} className="!hidden md:!flex !gap-4 text-sm"/>
                                <div className="hidden md:flex items-center gap-2 shrink-0">
                                    <HeaderButtons dark={dark}/>
                                </div>
                                <div className="md:hidden flex items-center shrink-0">
                                    <HamburgerBtn dark={dark}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Morphing Pill: starts full-width transparent, morphs to floating pill on scroll --
    if (variant === 'morphing-pill') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        const morphedPillBg = bgScrolled || '#ffffff';
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerVisibilityClass}`}
                    style={transitionStyle}>
                    <AnnouncementBar/>
                    <div className={`transition-all duration-500 ${scrolled ? 'px-4 pt-2' : 'px-0 pt-0'}`}>
                        <div className={`transition-all duration-500 mx-auto ${scrolled ? 'max-w-4xl rounded-full shadow-2xl px-5' : `${maxWidthClass} shadow-none ${paddingXClass}`}`}
                            style={{
                                backgroundColor: scrolled ? morphedPillBg : (currentBgStyle || 'transparent'),
                                backdropFilter: scrolled || currentBlur ? 'blur(16px)' : 'none',
                                WebkitBackdropFilter: scrolled || currentBlur ? 'blur(16px)' : 'none',
                                ...(!scrolled ? radiusStyle : {}),
                            }}>
                            <div className={`flex justify-between items-center transition-all duration-500 ${scrolled ? 'h-12' : hClass}`}>
                                <LogoBlock dark={dark}/>
                                <NavList dark={dark}/>
                                <div className="flex items-center gap-3 shrink-0">
                                    <PhoneLink cls={`hidden md:inline-flex ${dark ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}/>
                                    <HeaderButtons dark={dark}/>
                                    <HamburgerBtn dark={dark}/>
                                </div>
                            </div>
                        </div>
                    </div>
                    {!scrolled && <ProgressBar/>}
                    {!scrolled && <BorderLine/>}
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Hub Centered: large centered logo + nav bar below, premium editorial --
    if (variant === 'hub-centered') {
        const dark = scrolled ? scrolledIsDark : initialIsDark;
        return (
            <>{sharedCss}
                <nav aria-label="Menu principal" className={`${positionClass} top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass} ${headerVisibilityClass}`}
                    style={{ backgroundColor: currentBgStyle, ...transitionStyle, ...radiusStyle }}>
                    <AnnouncementBar/>
                    <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                        {/* Mobile: standard row */}
                        <div className={`flex md:hidden justify-between items-center ${hClass} ${shrinkClass}`}>
                            <LogoBlock dark={dark}/>
                            <HamburgerBtn dark={dark}/>
                        </div>
                        {/* Desktop: large logo centered + nav below */}
                        <div className={`hidden md:block transition-all duration-300 ${scrolled ? 'py-1' : 'pt-6 pb-2'}`}>
                            <div className="flex justify-center mb-3">
                                <a href="/" className="flex flex-col items-center gap-2 no-underline hover:opacity-80 transition-opacity">
                                    {(brand.logo_url || brand.logo_white_url) && (
                                        <div className="relative">
                                            {brand.logo_url && <img src={brand.logo_url} alt={brand.name} decoding="async" sizes="200px" className={`w-auto object-contain transition-all duration-300 ${scrolled ? 'h-8' : 'h-16'} ${dark && !scrolled ? 'opacity-0' : 'opacity-100'}`}/>}
                                            {brand.logo_white_url && <img src={brand.logo_white_url} alt={brand.name} decoding="async" sizes="200px" className={`w-auto object-contain transition-all duration-300 absolute inset-0 ${scrolled ? 'h-8' : 'h-16'} ${dark && !scrolled ? 'opacity-100' : 'opacity-0'}`}/>}
                                        </div>
                                    )}
                                    <span className={`font-bold tracking-tight transition-all duration-300 ${scrolled ? 'text-lg' : 'text-3xl'} ${dark ? 'text-white' : 'text-gray-900'}`}
                                        style={{color: hc.logo_text_color || undefined}}>{brand.name}</span>
                                </a>
                            </div>
                            {/* Nav bar with top border */}
                            <div className={`flex justify-center items-center gap-2 py-2 border-t ${dark ? 'border-white/15' : 'border-gray-200'}`}>
                                <NavList dark={dark}/>
                                <HeaderButtons dark={dark}/>
                            </div>
                        </div>
                    </div>
                    <ProgressBar/>
                    <BorderLine/>
                </nav>
                <MobileMenu/>
            </>
        );
    }

    // -- Default: transparent / solid / colored --
    const dark = scrolled ? scrolledIsDark : initialIsDark;
    return (
        <>
            {sharedCss}
            {enableAnimations && <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-white focus:text-gray-900 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-medium">Saltar al contenido</a>}
            <nav aria-label="Menu principal" className={`${positionClass} top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass} ${headerVisibilityClass}`}
                style={{ backgroundColor: currentBgStyle, ...transitionStyle, ...radiusStyle }}>
                <AnnouncementBar/>
                <div className={`${maxWidthClass} mx-auto ${paddingXClass}`}>
                    <div className={`flex justify-between items-center ${hClass} ${shrinkClass}`}>
                        <LogoBlock dark={dark}/>

                        <NavList dark={dark}/>

                        <div className="flex items-center gap-3 shrink-0">
                            <PhoneLink cls={`hidden md:inline-flex ${dark ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}/>
                            <HeaderButtons dark={dark}/>
                            <HamburgerBtn dark={dark}/>
                        </div>
                    </div>
                </div>
                <ProgressBar/>
                <BorderLine/>
            </nav>

            <MobileMenu/>
        </>
    );
}
